import React, { useEffect, useRef, useState } from 'react';

import { XTRAM_BOT_ALLOWED_EXTENSIONS, XTRAM_BOT_MAX_UPLOAD_MB } from '../../env';

import UiHeader from '../components/UiHeader';
import MessagesBody from '../components/MessagesBody';
import ChatInput from '../components/ChatInput';
import Sidebar from '../components/Sidebar';
import useSendMessage from '../hooks/chat/useSendMessage';
import useSendVoiceMessage from '../hooks/chat/useSendVoiceMessage';
import useFetchChatHistory from '../hooks/chat/useFetchChatHistory';
import useFetchSessionStatus from '../hooks/chat/useFetchSessionStatus';
import useUploadDocument from '../hooks/chat/useUploadDocument';

const stringifyContent = (value) => {
  if (typeof value === 'string') return value;
  if (value === null || value === undefined) return '';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const normalizeAssistantPayload = (payload) => {
  const body = payload?.data ?? payload;

  if (body && typeof body === 'object' && !Array.isArray(body)) {
    const answer = body.answer ?? body.response ?? body.message ?? '';
    const answerHtml = body.answer_html ?? body.response_html ?? '';

    return {
      text: stringifyContent(answer),
      html: typeof answerHtml === 'string' ? answerHtml : stringifyContent(answerHtml),
    };
  }

  return { text: stringifyContent(body), html: '' };
};

const isSessionExpiredError = (error) => {
  const status = error?.data?.session_status || error?.session_status;
  const message = error?.data?.error || error?.message || '';
  return status === 'expired' || /session\s+has\s+expired/i.test(String(message));
};

const getFileExtension = (filename = '') => {
  const idx = filename.lastIndexOf('.');
  if (idx === -1) return '';
  return filename.slice(idx + 1).toLowerCase();
};

export default function ChatbotUI({ compact = false, hideSidebar = false }) {
  const { simpleAsk, simpleAskStream, isPending } = useSendMessage();
  const { mutate: sendVoiceMessage, isPending: isVoicePending } = useSendVoiceMessage();
  const { uploadDocument, isPending: isUploadPending } = useUploadDocument();

  const allowedExtensions = XTRAM_BOT_ALLOWED_EXTENSIONS;
  const maxUploadMb = XTRAM_BOT_MAX_UPLOAD_MB;
  const maxUploadBytes = maxUploadMb * 1024 * 1024;

  const createSessionId = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    // Fallback UUIDv4 for non-secure contexts / older browsers.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const historyLoadedRef = useRef(false);

  const isValidSessionId = (id) =>
    typeof id === 'string' &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);

  const [sessionId, setSessionId] = useState(() => {
    const storedSessionId = localStorage.getItem('chat_session_id');

    try {
      const saved = localStorage.getItem('chat_sessions');
      const sessions = (saved ? JSON.parse(saved) : []).filter(isValidSessionId);

      if (isValidSessionId(storedSessionId) && sessions.includes(storedSessionId)) return storedSessionId;
      if (sessions.length > 0) return sessions[0];
    } catch {
      // ignore
    }

    return createSessionId();
  });

  const [expiredSessionId, setExpiredSessionId] = useState(null);

  const { data: sessionStatusData, isLoading: isSessionStatusLoading, isError: isSessionStatusError } =
    useFetchSessionStatus(sessionId);

  const isCurrentSessionExpired = sessionStatusData?.status === 'expired';
  const shouldLoadHistory = !isSessionStatusLoading && (!isCurrentSessionExpired || isSessionStatusError);

  const { data: historyData } = useFetchChatHistory(sessionId, shouldLoadHistory);

  const [savedSessions, setSavedSessions] = useState(() => {
    try {
      const saved = localStorage.getItem('chat_sessions');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const messagesEndRef = useRef(null);
  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  useEffect(() => {
    scrollToBottom();
  }, [messages, isPending]);

  useEffect(() => {
    if (!sessionId) return;

    localStorage.setItem('chat_session_id', sessionId);
    setSavedSessions((prev) => {
      if (prev.includes(sessionId)) return prev;
      const newSessions = [sessionId, ...prev];
      localStorage.setItem('chat_sessions', JSON.stringify(newSessions));
      return newSessions;
    });
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId || !isCurrentSessionExpired || expiredSessionId === sessionId) return;

    setExpiredSessionId(sessionId);
    setMessages([]);

    setSavedSessions((prev) => {
      const updated = prev.filter((id) => id !== sessionId);
      localStorage.setItem('chat_sessions', JSON.stringify(updated));
      return updated;
    });

    setSessionId(createSessionId());
  }, [sessionId, isCurrentSessionExpired, expiredSessionId]);

  const handleRemoveSession = (idToRemove, e) => {
    e?.stopPropagation();
    const updatedSessions = savedSessions.filter((id) => id !== idToRemove);
    setSavedSessions(updatedSessions);
    localStorage.setItem('chat_sessions', JSON.stringify(updatedSessions));

    if (sessionId === idToRemove) {
      const nextSession = updatedSessions.length > 0 ? updatedSessions[0] : createSessionId();
      setSessionId(nextSession);
    }
  };

  useEffect(() => {
    if (historyData && historyData.entries && !historyLoadedRef.current) {
      const normalizeHistoryUserQuestion = (raw) => {
        if (typeof raw !== 'string') return { content: stringifyContent(raw), voiceMessage: false, voiceTranscript: null };
        const trimmed = raw.trim();
        const quoted = trimmed.match(/^🎤\s*"\s*([\s\S]*?)\s*"$/);
        if (quoted) return { content: '', voiceMessage: true, voiceTranscript: quoted[1] };
        if (trimmed === '🎤 Voice message' || /^🎤\s*Voice message/i.test(trimmed)) {
          return { content: '', voiceMessage: true, voiceTranscript: null };
        }
        return { content: raw, voiceMessage: false, voiceTranscript: null };
      };

      const chatMessages = historyData.entries.flatMap((entry, index) => {
        const msgs = [];
        if (entry.question) {
          const u = normalizeHistoryUserQuestion(entry.question);
          msgs.push({
            id: `hist-user-${index}`,
            type: 'user',
            content: u.content,
            ...(u.voiceMessage ? { voiceMessage: true, voiceTranscript: u.voiceTranscript } : {}),
            timestamp: new Date(entry.timestamp * 1000),
          });
        }
        if (entry.answer || entry.answer_html) {
          msgs.push({
            id: `hist-assistant-${index}`,
            type: 'assistant',
            content: stringifyContent(entry.answer),
            htmlContent: entry.answer_html || '',
            timestamp: new Date(entry.timestamp * 1000),
          });
        }
        return msgs;
      });

      const documentMessages = (historyData.documents ?? []).map((doc) => ({
        id: `hist-doc-${doc.document_id}`,
        type: 'user',
        fileName: doc.original_filename || doc.stored_filename,
        fileSize: null,
        uploadStatus: 'success',
        timestamp: new Date(doc.uploaded_at * 1000),
      }));

      const allMessages = [...chatMessages, ...documentMessages].sort((a, b) => a.timestamp - b.timestamp);
      setMessages(allMessages);
      historyLoadedRef.current = true;
    }
  }, [historyData]);

  useEffect(() => {
    setMessages([]);
    historyLoadedRef.current = false;
  }, [sessionId]);

  const handleNewSession = () => {
    setSessionId(createSessionId());
    setMessages([]);
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const activeSessionId = sessionId?.trim() || createSessionId();
    if (activeSessionId !== sessionId) setSessionId(activeSessionId);

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: input,
      timestamp: new Date(),
    };

    const assistantId = Date.now() + 1;
    const assistantPlaceholder = {
      id: assistantId,
      type: 'assistant',
      content: '',
      htmlContent: '',
      streaming: true,
      timestamp: new Date(),
    };

    // Add both the user message AND an empty streaming assistant bubble up front,
    // so the user sees a live bubble with a typing indicator immediately while tokens flow in.
    setMessages((prev) => [...prev, userMessage, assistantPlaceholder]);
    const currentInput = input;
    setInput('');

    const startTime = Date.now();
    let streamingText = '';
    let assistantAdded = true;

    const recordResponseTime = () => {
      const responseTime = Date.now() - startTime;
      const prevAvg = parseInt(localStorage.getItem('avg_response_time') || '5000', 10);
      const prevCount = parseInt(localStorage.getItem('response_count') || '0', 10);
      const newCount = prevCount + 1;
      const newAvg = Math.round((prevAvg * prevCount + responseTime) / newCount);
      localStorage.setItem('avg_response_time', newAvg.toString());
      localStorage.setItem('response_count', newCount.toString());
    };

    const stripAssistantIfStreaming = () => {
      if (!assistantAdded) return;
      assistantAdded = false;
      setMessages((prev) => prev.filter((m) => m.id !== assistantId));
    };

    try {
      await simpleAskStream(currentInput, activeSessionId, {
        onFirstToken: () => {
          // Bubble is already on screen; nothing to do here.
        },
        onToken: (chunk) => {
          streamingText += chunk;
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId ? { ...m, content: streamingText, streaming: true } : m
            )
          );
        },
        onFinal: (payload) => {
          const normalized = normalizeAssistantPayload(payload);
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantId
                ? {
                    ...m,
                    content: normalized.text,
                    htmlContent: normalized.html,
                    streaming: false,
                  }
                : m
            )
          );
          recordResponseTime();
        },
      });
    } catch (error) {
      stripAssistantIfStreaming();
      let usedFallback = false;
      try {
        const response = await simpleAsk(currentInput, activeSessionId);
        const normalized = normalizeAssistantPayload(response);
        const aiMessage = {
          id: assistantId,
          type: 'assistant',
          content: normalized.text,
          htmlContent: normalized.html,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, aiMessage]);
        recordResponseTime();
        usedFallback = true;
      } catch {
        // fall through to session / generic handling below
      }
      if (usedFallback) {
        return;
      }
      // eslint-disable-next-line no-console
      console.error('Error sending message:', error);

      if (isSessionExpiredError(error)) {
        const freshSessionId = createSessionId();
        setSessionId(freshSessionId);

        try {
          const retryResponse = await simpleAsk(currentInput, freshSessionId);
          const normalizedRetry = normalizeAssistantPayload(retryResponse);

          const aiRetryMessage = {
            id: Date.now() + 1,
            type: 'assistant',
            content: normalizedRetry.text,
            htmlContent: normalizedRetry.html,
            timestamp: new Date(),
          };
          setMessages((prev) => [...prev, aiRetryMessage]);
          return;
        } catch (retryError) {
          // eslint-disable-next-line no-console
          console.error('Retry after session expiration failed:', retryError);
          setMessages((prev) => [
            ...prev,
            {
              id: Date.now() + 1,
              type: 'assistant',
              content:
                'Your previous session expired. I created a new session, but this request could not be completed. Please send it again.',
              timestamp: new Date(),
            },
          ]);
          return;
        }
      }

      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          type: 'assistant',
          content: "Sorry, I encountered an error processing your message. Please try again.",
          timestamp: new Date(),
        },
      ]);
    }
  };

  const handleVoiceSend = async (audioBlob) => {
    const activeSessionId = sessionId?.trim() || createSessionId();
    if (activeSessionId !== sessionId) setSessionId(activeSessionId);

    const audioUrl = URL.createObjectURL(audioBlob);

    const userMessage = {
      id: Date.now(),
      type: 'user',
      voiceMessage: true,
      content: '',
      voiceTranscript: null,
      audioUrl,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);

    sendVoiceMessage(
      { audioBlob, session_id: activeSessionId },
      {
        onSuccess: (response) => {
          const normalized = normalizeAssistantPayload(response);

          if (response?.transcript) {
            setMessages((prev) =>
              prev.map((msg) =>
                msg.id === userMessage.id ? { ...msg, voiceTranscript: String(response.transcript) } : msg
              )
            );
          }

          setMessages((prev) => [
            ...prev,
            {
              id: Date.now() + 1,
              type: 'assistant',
              content: normalized.text || 'I received your voice message.',
              htmlContent: normalized.html,
              timestamp: new Date(),
            },
          ]);
        },
        onError: (error) => {
          // eslint-disable-next-line no-console
          console.error('Error sending voice message:', error);
          if (isSessionExpiredError(error)) {
            setSessionId(createSessionId());
            setMessages((prev) => [
              ...prev,
              {
                id: Date.now() + 1,
                type: 'assistant',
                content: 'Your previous session expired. I created a new session. Please resend your voice message.',
                timestamp: new Date(),
              },
            ]);
            return;
          }
          setMessages((prev) => [
            ...prev,
            {
              id: Date.now() + 1,
              type: 'assistant',
              content: "Sorry, I couldn't process your voice message. Please try again.",
              timestamp: new Date(),
            },
          ]);
        },
      }
    );
  };

  const handleFileUpload = async (file) => {
    if (!file) return;

    const extension = getFileExtension(file.name);
    if (!allowedExtensions.includes(extension)) {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          type: 'assistant',
          content: `Unsupported file type .${extension || 'unknown'}. Allowed: ${allowedExtensions.join(', ')}`,
          timestamp: new Date(),
        },
      ]);
      return;
    }

    if (file.size > maxUploadBytes) {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          type: 'assistant',
          content: `File is too large. Maximum size is ${maxUploadMb} MB.`,
          timestamp: new Date(),
        },
      ]);
      return;
    }

    const activeSessionId = sessionId?.trim() || createSessionId();
    if (activeSessionId !== sessionId) setSessionId(activeSessionId);

    const fileMessageId = Date.now();
    const userFileMessage = {
      id: fileMessageId,
      type: 'user',
      fileName: file.name,
      fileSize: file.size,
      uploadStatus: 'uploading',
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userFileMessage]);

    const setUploadStatus = (status) => {
      setMessages((prev) => prev.map((msg) => (msg.id === fileMessageId ? { ...msg, uploadStatus: status } : msg)));
    };

    try {
      await uploadDocument(file, activeSessionId);
      setUploadStatus('success');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error uploading file:', error);

      if (isSessionExpiredError(error)) {
        const freshSessionId = createSessionId();
        setSessionId(freshSessionId);

        try {
          await uploadDocument(file, freshSessionId);
          setUploadStatus('success');
          return;
        } catch (retryError) {
          // eslint-disable-next-line no-console
          console.error('Retry file upload after session expiration failed:', retryError);
        }
      }

      setUploadStatus('error');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div style={{ display: 'flex', background: '#fff', height: '100%', width: '100%' }}>
      {!compact && !hideSidebar ? (
        <Sidebar
          sessionId={sessionId}
          savedSessions={savedSessions}
          onSessionChange={setSessionId}
          onNewSession={handleNewSession}
          onRemoveSession={handleRemoveSession}
          onFileUpload={handleFileUpload}
          isUploadingFile={isUploadPending}
          acceptedFileTypes={allowedExtensions}
          maxFileSizeMB={maxUploadMb}
          documents={historyData?.documents ?? []}
        />
      ) : null}

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
        {!compact && !hideSidebar ? <UiHeader /> : null}

        <MessagesBody messages={messages} isTyping={isPending || isVoicePending} messagesEndRef={messagesEndRef} compact={compact} />

        <ChatInput
          input={input}
          setInput={setInput}
          handleKeyPress={handleKeyPress}
          handleSend={handleSend}
          onVoiceSend={handleVoiceSend}
          onFileUpload={handleFileUpload}
          disabled={isPending || isVoicePending || isUploadPending}
          isUploadingFile={isUploadPending}
          acceptedFileTypes={allowedExtensions}
          maxFileSizeMB={maxUploadMb}
          compact={compact}
        />
      </div>
    </div>
  );
}

